// HoverShell — affordance wrapper for inline-edit cells.
//
// Subtle grey-soft background on hover, edit/select/open icon that
// escalates from 35% to 90% opacity on hover. Keyboard-accessible
// (Enter/Space). Used by EditableNumberCell + EditablePriceCell, and
// any future @ds/tables/cells/* cell that wraps a display row with a
// click-to-edit affordance.
//
// Promoted to `@ds/editable/` from the legacy `src/components/ui/Editable.tsx`
// so DS-tier consumers no longer reach UP into the legacy UI module.
// The legacy file re-exports from here for backwards compatibility with
// non-DS consumers (`src/pages/transactions/list/PipelineColumns.tsx` etc.).
//
// Bespoke leaf chrome: HoverShell is the affordance primitive itself —
// negative-margin compensation ("padding: 2px 6px / margin: -2px -6px"),
// hover-tracked opacity state, and `role="button"` keyboard handling all
// require raw spans + inline `style`. Also intentionally accepts a `style`
// prop on its public API so consumers can layer width/inline-flex tweaks
// at the call site. Whole file kept raw per migration policy — flagged.

import React, { useState } from "react";
import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import {
  ArrowRightOutlined,
  DownOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { FONT_FAMILY, FONT_SIZE, RADIUS, SPACE, SURFACE, TEXT } from "@ds/tokens/palette";

export type AffordanceKind = "edit" | "select" | "open" | "none";

// ─── Inputs ──────────────────────────────────────────────────────────────

export interface HoverShellInput {
  children:    ReactNode;
  /** Trailing affordance icon. `"none"` keeps the hover background + click +
   *  keyboard semantics but renders no icon — used when an outer container
   *  (KVGrid) already shows the affordance icon next to the label. */
  affordance:  AffordanceKind;
  active?:     boolean;
  disabled?:   boolean;
  style?:      CSSProperties;
}

// ─── Outputs ─────────────────────────────────────────────────────────────

export interface HoverShellOutput {
  onClick: () => void;
}

export type HoverShellProps = HoverShellInput & HoverShellOutput;

// ─── Component ───────────────────────────────────────────────────────────

const AFFORDANCE_ICON: Record<Exclude<AffordanceKind, "none">, ReactNode> = {
  edit:   <EditOutlined        style={{ fontSize: FONT_SIZE.label }} />,
  select: <DownOutlined        style={{ fontSize: FONT_SIZE.small }} />,
  open:   <ArrowRightOutlined  style={{ fontSize: FONT_SIZE.label }} />,
};

export function HoverShell({
  children, affordance, onClick,
  active = false, disabled = false, style,
}: HoverShellProps) {
  const [hovered, setHovered] = useState(false);
  const show = hovered || active;

  if (disabled) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: SPACE.xs + 2,
          fontFamily: FONT_FAMILY.sans,
          ...style,
        }}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      onKeyDown={(e: KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: SPACE.xs + 2,
        padding: "2px 6px",
        margin: "-2px -6px",
        borderRadius: RADIUS.xs + 1,
        cursor: "pointer",
        background: show ? SURFACE.surfaceHover : "transparent",
        transition: "background 100ms ease",
        outline: "none",
        fontFamily: FONT_FAMILY.sans,
        ...style,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: SPACE.xs + 2 }}>
        {children}
      </span>
      {affordance !== "none" && (
        <span
          style={{
            opacity: show ? 0.9 : 0.35,
            transition: "opacity 120ms ease",
            display: "inline-flex",
            alignItems: "center",
            color: TEXT.muted,
          }}
        >
          {AFFORDANCE_ICON[affordance]}
        </span>
      )}
    </span>
  );
}
